import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Button } from './ui/button';
import { 
  UserIcon, 
  GraduationCapIcon,
  SparklesIcon,
  ExternalLinkIcon
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfilePage = () => {
  const { userId } = useParams();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchUserProfile();
    // Show modal after a brief delay
    setTimeout(() => setShowModal(true), 500);
  }, [userId]);

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}`);
      setUserProfile(response.data);
    } catch (error) {
      console.error('Error fetching user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const handleViewFullProfile = () => {
    window.open(`${window.location.origin}/community/${userId}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white text-xl loading-dots">Loading profile...</div>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white text-xl">Profile not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      
      {/* Blurred Background - Community Hub Preview */}
      <div className="absolute inset-0 blur-overlay">
        <div className="min-h-screen p-4">
          <div className="max-w-6xl mx-auto space-y-8 pt-20">
            
            {/* Mock Community Content */}
            <div className="text-center space-y-4">
              <h1 className="text-4xl font-bold gradient-text">OneTap Community</h1>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="glass-dark">
                <CardContent className="p-6">
                  <div className="space-y-3">
                    <div className="w-12 h-12 bg-white/10 rounded-lg"></div>
                    <div className="space-y-2">
                      <div className="h-4 bg-white/10 rounded w-3/4"></div>
                      <div className="h-6 bg-white/10 rounded w-1/2"></div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-dark">
                <CardContent className="p-6">
                  <div className="space-y-3">
                    <div className="w-12 h-12 bg-white/10 rounded-lg"></div>
                    <div className="space-y-2">
                      <div className="h-4 bg-white/10 rounded w-2/3"></div>
                      <div className="h-6 bg-white/10 rounded w-1/3"></div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-dark">
                <CardContent className="p-6">
                  <div className="space-y-3">
                    <div className="w-12 h-12 bg-white/10 rounded-lg"></div>
                    <div className="space-y-2">
                      <div className="h-4 bg-white/10 rounded w-1/2"></div>
                      <div className="h-6 bg-white/10 rounded w-3/4"></div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="glass-dark">
              <CardContent className="p-8">
                <div className="space-y-4">
                  <div className="h-6 bg-white/10 rounded w-1/4"></div>
                  <div className="grid grid-cols-3 gap-4">
                    {[1,2,3].map(i => (
                      <div key={i} className="space-y-3">
                        <div className="w-16 h-16 bg-white/10 rounded-full mx-auto"></div>
                        <div className="space-y-2">
                          <div className="h-3 bg-white/10 rounded w-3/4 mx-auto"></div>
                          <div className="h-3 bg-white/10 rounded w-1/2 mx-auto"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </div>

      {/* Modal Overlay */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center modal-backdrop">
          <Card className="profile-card max-w-md w-full mx-4 onetap-glow" data-testid="profile-modal">
            <CardContent className="p-8">
              <div className="text-center space-y-6">
                
                {/* Profile Avatar */}
                <div className="relative">
                  <Avatar className="w-24 h-24 mx-auto bg-gradient-to-br from-white to-gray-300">
                    <AvatarFallback className="text-black font-bold text-2xl">
                      {getInitials(userProfile.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-white/20 text-white border-white/30">
                      OneTap
                    </Badge>
                  </div>
                </div>

                {/* Profile Info */}
                <div className="space-y-4">
                  <div>
                    <h2 className="text-2xl font-bold gradient-text-accent mb-2">
                      {userProfile.name}
                    </h2>
                    <div className="flex items-center justify-center space-x-2 text-white/80">
                      <GraduationCapIcon className="w-4 h-4" />
                      <span>{userProfile.year_of_study}</span>
                    </div>
                  </div>

                  {/* Interests Preview */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-center space-x-2 text-white/70">
                      <SparklesIcon className="w-4 h-4" />
                      <span className="text-sm">Interests</span>
                    </div>
                    <div className="flex flex-wrap justify-center gap-2">
                      {userProfile.interests.slice(0, 3).map((interest, index) => (
                        <Badge
                          key={index}
                          className="interest-chip text-xs"
                        >
                          {interest}
                        </Badge>
                      ))}
                      {userProfile.interests.length > 3 && (
                        <Badge className="interest-chip text-xs">
                          +{userProfile.interests.length - 3}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button 
                    className="btn-primary w-full"
                    onClick={handleViewFullProfile}
                    data-testid="view-full-profile-btn"
                  >
                    <ExternalLinkIcon className="w-4 h-4 mr-2" />
                    View Full Profile
                  </Button>
                  
                  <div className="text-center">
                    <p className="text-white/60 text-xs">
                      Member since {new Date(userProfile.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

              </div>
            </CardContent>
          </Card>
        </div>
      )}

    </div>
  );
};

export default ProfilePage;